<template>
  <div
    v-if="visible && activeFamily"
    ref="chooserEl"
    class="engraving-chooser"
    :class="`engraving-chooser--${activeFamily.key}`"
    role="group"
    aria-labelledby="engraving-chooser-heading"
    :style="position"
  >
    <h2 id="engraving-chooser-heading">{{ activeFamily.title }}</h2>
    <div class="engraving-chooser__options">
      <button
        v-for="shape in visibleChoices"
        :key="shape.id"
        type="button"
        :aria-label="shape.label"
        :aria-pressed="selectedChoiceId === shape.id"
        :disabled="!isColorAvailable(shape)"
        :aria-disabled="!isColorAvailable(shape) ? 'true' : undefined"
        @click="selectChoice(shape)"
      >
        <NewStudioLayeredPickerIcon v-if="isLayeredShape(shape)" :pattern="shape.prefix.replace('Layered', '')" />
        <component v-else :is="shapeComponents[shape.id]" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LAYERED_ENGRAVED_SHAPES } from '~/composables/shapes/layeredEngraving'
import { isLayeredShape } from '~/shared/layeredEngraving'
import { ENGRAVED_1X1_SQUARE_SHAPES } from '~/composables/shapes/shapesEngraved1x1Square'
import { ENGRAVED_2X2_SQUARE_SHAPES } from '~/composables/shapes/shapesEngraved2x2Square'
import { getColorVariation } from '~/composables/shapes/utils'
import type { Shape } from '~/types/grid.types'

const gridStore = useGridStore()
const userStore = useUserStore()
const projectStore = useProjectStore()
const interactions = useShapeInteractions()
const families = [
  { key: 'layered', title: "18 MM LAYERED ENGRAVING TYPE", choices: LAYERED_ENGRAVED_SHAPES },
  {
    key: '1x1',
    title: "1' X 1' ENGRAVING TYPE",
    choices: ENGRAVED_1X1_SQUARE_SHAPES,
  },
  {
    key: '2x2',
    title: "2' X 2' ENGRAVING TYPE",
    choices: ENGRAVED_2X2_SQUARE_SHAPES,
  },
] as const
const engravingGrids = new Set(['square', '45-deg', 'freeform'])
const normalizeShapeId = (shapeId: string) => shapeId.replace(/45deg$|Freeform$/, '')
const familyForShape = (shapeId: string | undefined) => {
  if (!shapeId) return null
  const baseId = normalizeShapeId(shapeId)
  return families.find(family => family.choices.some(shape => {
    const baseShape = normalizeShapeId(shape.id)
    return baseShape === baseId || shape.children?.some(child => normalizeShapeId(child.id) === baseId)
  })) || null
}
const selectedItem = computed(() => {
  if (!engravingGrids.has(gridStore.gridPattern?.id) || gridStore.selectedItemsOnGrid.length < 1) return null
  // Check that all selected items are engraved tiles from the same family
  let commonFamily: typeof families[number] | null = null
  for (const item of gridStore.selectedItemsOnGrid) {
    const shapeId = item.metadata?.shape_id || item.attrs?.metadata?.shape_id
    const family = familyForShape(shapeId)
    if (!family) return null
    if (!commonFamily) commonFamily = family
    else if (commonFamily.key !== family.key) return null
  }
  return gridStore.selectedItemsOnGrid[0]
})
const selectedShapeId = computed(() => selectedItem.value?.metadata?.shape_id || selectedItem.value?.attrs?.metadata?.shape_id)
const activeFamily = computed(() => familyForShape(selectedShapeId.value))
const visibleChoices = computed(() => activeFamily.value?.choices.filter(shape => !shape.admin || userStore.isAdmin) || [])
const isColorAvailable = (shape: Shape) => Boolean(gridStore.selectedColor && getColorVariation(shape, gridStore.selectedColor, { allowFallback: false }))
const selectedChoiceId = computed(() => {
  if (!selectedShapeId.value) return undefined
  const baseId = normalizeShapeId(selectedShapeId.value)
  return activeFamily.value?.choices.find(shape => normalizeShapeId(shape.id) === baseId || shape.children?.some(child => normalizeShapeId(child.id) === baseId))?.id
})
const selectionKey = computed(() => {
  if (!selectedItem.value) return null
  const ids = gridStore.selectedItemsOnGrid.map(i => i.id).sort().join(',')
  return `${ids}:${selectedShapeId.value}`
})
const dismissedSelectionKey = ref<string | null>(null)
const dismissedByChange = ref(false)
const chooserEl = ref<HTMLElement | null>(null)
const visible = computed(() => Boolean(selectionKey.value && dismissedSelectionKey.value !== selectionKey.value))
const selectedTileIds = computed(() => gridStore.selectedItemsOnGrid.map(item => item.id).sort().join(','))
watch([selectionKey, selectedTileIds], ([next, ids], [prev, previousIds]) => {
  if (!next) {
    dismissedSelectionKey.value = null
    dismissedByChange.value = false
  }
  else if (dismissAfterChange.value) {
    dismissedSelectionKey.value = next
    dismissedByChange.value = true
    dismissAfterChange.value = false
  }
  else if (prev && next !== prev && ids === previousIds) {
    // Rotating changes shape_id, but does not select a different tile.
    // Carry dismissal forward rather than treating the orientation as a new selection.
    if (dismissedSelectionKey.value === prev) dismissedSelectionKey.value = next
  }
  else if (prev && next !== prev) {
    // Selection changed (added/removed tiles) — always reopen
    dismissedSelectionKey.value = null
    dismissedByChange.value = false
  }
})

// Close when clicking outside the chooser, or reopen if clicking on a selected tile
const onCanvasClick = (e: MouseEvent) => {
  if (chooserEl.value?.contains(e.target as Node)) return
  if (!visible.value && selectionKey.value) {
    // Only a click on a selected canvas tile may reopen a dismissed chooser.
    // Palette and toolbar clicks must not toggle it just because selection persists.
    if (!(e.target instanceof Element) || !e.target.closest('.konvajs-content')) return
    const ref = projectStore.gridRef as any
    const stage = ref?.getStage?.() || ref
    if (!stage?.getIntersection) return
    const rect = stage.container().getBoundingClientRect()
    let node = stage.getIntersection({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    const selectedIds = new Set(gridStore.selectedItemsOnGrid.map(item => item.id))
    while (node && !selectedIds.has(node.id())) node = node.getParent()
    if (!node) return
    // Popup was dismissed and the selected tile was clicked — reopen
    dismissedSelectionKey.value = null
    dismissedByChange.value = false
    nextTick(updatePosition)
    return
  }
  if (!visible.value) return
  dismissedSelectionKey.value = selectionKey.value || null
  dismissedByChange.value = false
}
onMounted(() => document.addEventListener('pointerdown', onCanvasClick))
onUnmounted(() => document.removeEventListener('pointerdown', onCanvasClick))
const dismissAfterChange = ref(false)
const selectChoice = (shape: Shape) => {
  if (!isColorAvailable(shape)) return
  if (selectedChoiceId.value === shape.id) {
    dismissedSelectionKey.value = selectionKey.value || null
    return
  }
  dismissAfterChange.value = true
  interactions.changeSelectedShape(shape)
}
const positionStyle = ref({ left: '0px', top: '0px', position: 'fixed' as const, maxWidth: 'calc(100vw - 24px)' })

const updatePosition = () => {
  if (!selectedItem.value) return
  const ref = projectStore.gridRef as any
  const stage = ref?.getStage?.() || ref
  if (!stage?.findOne) return
  const canvas = stage.container().getBoundingClientRect()
  const bounds = gridStore.shapeList.map(item => stage.findOne(`#${item.id}`)?.getClientRect()).filter(Boolean)
  if (!bounds.length) return
  const left = Math.min(...bounds.map(b => b.x))
  const right = Math.max(...bounds.map(b => b.x + b.width))
  const bottom = Math.max(...bounds.map(b => b.y + b.height))
  const margin = 12
  const maxWidth = Math.max(0, Math.min(canvas.right, window.innerWidth) - Math.max(canvas.left, 0) - margin * 2)
  const width = Math.min(chooserEl.value?.scrollWidth || 774, maxWidth)
  const minLeft = Math.max(canvas.left, 0) + margin
  const maxLeft = Math.min(canvas.right, window.innerWidth) - width - margin
  positionStyle.value = {
    left: `${Math.max(minLeft, Math.min(canvas.left + (left + right) / 2 - width / 2, maxLeft))}px`,
    // Never move the chooser upward over tiles to fit the viewport.
    top: `${canvas.top + bottom + margin}px`,
    position: 'fixed',
    maxWidth: `${maxWidth}px`,
  }
}
const position = computed(() => positionStyle.value)
watch(visible, isVisible => { if (isVisible) nextTick(updatePosition) })
watch(selectionKey, () => nextTick(updatePosition))
watch([() => gridStore.gridPos, () => gridStore.gridZoom, () => gridStore.shapeList.map(item => [item.id, item.x, item.y, item.rotation, item.width, item.height])], () => {
  if (visible.value) nextTick(updatePosition)
}, { deep: true })
onMounted(() => window.addEventListener('resize', updatePosition))
onUnmounted(() => window.removeEventListener('resize', updatePosition))
const svgModules = import.meta.glob('../Shapes/SVG/square/*.svg', { eager: true, query: '?component', import: 'default' })
const shapeComponents = Object.fromEntries(families.flatMap(family => family.choices).map(shape => [
  shape.id,
  svgModules[`../Shapes/SVG/square/${shape.icon}.svg`],
]))
</script>

<style scoped>
.engraving-chooser {
  position: fixed;
  z-index: 1000;
  padding: 16px;
  border: 1px solid #e7e6e1;
  border-radius: 8px;
  background: #fff;
  box-shadow:
    0 17px 5px rgba(0, 0, 0, 0),
    0 11px 4px rgba(0, 0, 0, 0.02),
    0 6px 4px rgba(0, 0, 0, 0.05),
    0 3px 3px rgba(0, 0, 0, 0.09),
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 3px 10px rgba(20, 20, 22, 0.1);
}

h2 {
  margin: 0 0 10px;
  color: #9a9994;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.engraving-chooser__options { display: flex; gap: 12px; overflow-x: auto; padding: 2px; }
.engraving-chooser__options button { flex: 0 0 75px; }
.engraving-chooser button {
  display: grid;
  width: 75px;
  height: 75px;
  padding: 4px;
  border: 2.4px solid #8f8e89;
  border-radius: 0;
  background: #fff;
  place-items: center;
  cursor: pointer;
}
.engraving-chooser button[aria-pressed="true"] { border-color: #2f365d; background: #ccd5da; }
.engraving-chooser button:not(:disabled):not([aria-pressed="true"]):hover { border-color: #b8c6ce; background: #dfe4e5; }
.engraving-chooser button:disabled { cursor: not-allowed; opacity: .45; }
.engraving-chooser button :deep(svg) { width: 100%; height: 100%; }
.engraving-chooser button :deep(path),
.engraving-chooser button :deep(rect),
.engraving-chooser button :deep(line),
.engraving-chooser button :deep(circle),
.engraving-chooser button :deep(ellipse) { stroke: #8f8e89; }
.engraving-chooser button[aria-pressed="true"] :deep(path),
.engraving-chooser button[aria-pressed="true"] :deep(rect),
.engraving-chooser button[aria-pressed="true"] :deep(line),
.engraving-chooser button[aria-pressed="true"] :deep(circle),
.engraving-chooser button[aria-pressed="true"] :deep(ellipse) { stroke: #2f365d; }
.engraving-chooser button:focus-visible { outline: 2px solid var(--studio-focus); outline-offset: 2px; }
.engraving-chooser { width: max-content; }
.engraving-chooser--layered h2 { line-height: 1.5; }
.engraving-chooser--layered :deep(.shape-picker__layered-icon) { transform: scale(1.18); }
.engraving-chooser--layered button:is(:hover, :focus-visible, [aria-pressed="true"], :disabled) :deep([data-icon-state="default"]) { visibility: hidden; }
.engraving-chooser--layered button:not(:disabled):not([aria-pressed="true"]):is(:hover, :focus-visible) :deep([data-icon-state="hover"]),
.engraving-chooser--layered button[aria-pressed="true"]:not(:disabled) :deep([data-icon-state="selected"]),
.engraving-chooser--layered button:disabled :deep([data-icon-state="disabled"]) { visibility: visible; }
</style>
